/**
 * @file index.tsx
 *
 * @fileoverview Renders a component.
 */
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { PasswordInput } from '~/vcl/atoms/01-forms/03-input/01-password-input';
import PasswordFeedback from '~/vcl/atoms/01-forms/03-input/02-password-feedback';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import * as Yup from 'yup';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { getPasswordFeedbackMessages } from '~/jobiqo/components/PasswordPolicy/messages';
import { usePasswordPolicy } from '~/jobiqo/hooks/usePasswordPolicy';
import styles from './commonFields.module.scss';

interface RegisterCommonFieldsProps {}
export default function RegisterCommonFields({}: RegisterCommonFieldsProps) {
  const intl = useIntl();
  const { control } = useFormContext();
  const { minLength } = usePasswordPolicy();
  const passwordFeedbackMessages = getPasswordFeedbackMessages(intl, minLength);

  return (
    <div className={styles.commonFields}>
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState: { error, isTouched } }) => (
          <>
            <Input
              {...field}
              type="email"
              data-test-id="email_registration"
              placeholder={intl.formatMessage({
                id: 'register_email',
                defaultMessage: 'Enter your email',
              })}
              required={true}
            />
            {isTouched && error && (
              <>
                <InlineError message={error.message} />
                <Spacing size="small" />
              </>
            )}
          </>
        )}
      />
      <Spacing />
      <Controller
        name="password"
        control={control}
        render={({ field, fieldState: { error, isTouched } }) => (
          <>
            <PasswordInput
              {...field}
              data-test-id="password_registration"
              placeholder={intl.formatMessage({
                id: 'register_password',
                defaultMessage: 'Enter your password',
              })}
              required={true}
            />
            <Spacing size="small" />
            <PasswordFeedback
              password={typeof field.value === 'string' ? field.value : ''}
              messages={passwordFeedbackMessages}
              minLength={minLength}
            />
            {isTouched && error && (
              <>
                <InlineError message={error.message} />
                <Spacing size="small" />
              </>
            )}
          </>
        )}
      />
      <Spacing />
    </div>
  );
}

export function getCommonValidationSchema(intl: IntlShape) {
  const { EMAIL_VALIDATION, FIELD_REQUIRED } = getCommonTranslations();

  return {
    email: Yup.string().email(EMAIL_VALIDATION).required(FIELD_REQUIRED),
    password: Yup.string().required(FIELD_REQUIRED),
  };
}
